import { GlobalService } from "../modules";
import { convertCurrency, getCurrencyById } from "./currencyConversion";

interface CurrencyData {
    id: string;
    value: string;
    label: string;
    initial: string;
}

interface LocationData {
    countryCode: string;
    countryName: string;
}

const CURRENCY_CACHE_KEY = "user_currency_preference";
const LOCATION_CACHE_KEY = "user_location_data";
const COUNTRY_CACHE_KEY = "user_country_preference";
const CACHE_EXPIRY_HOURS = 24;

/**
 * Maps country codes to their corresponding currency codes
 */
const getCurrencyByCountry = (countryCode: string): string => {
    const currencyMap: { [key: string]: string } = {
        "US": "USD", // United States Dollar
        "CA": "CAD", // Canadian Dollar
        "GB": "GBP", // British Pound Sterling
        "EU": "EUR", // Eurozone (generic)
        "DE": "EUR", // Germany
        "ES": "EUR", // Spain
        "FR": "EUR", // France
        "IT": "EUR", // Italy
        "PT": "EUR", // Portugal
        "IE": "EUR", // Ireland
        "NL": "EUR", // Netherlands
        "BE": "EUR", // Belgium
        "LU": "EUR", // Luxembourg
        "AT": "EUR", // Austria
        "FI": "EUR", // Finland
        "GR": "EUR", // Greece
        "CY": "EUR", // Cyprus
        "MT": "EUR", // Malta
        "SI": "EUR", // Slovenia
        "SK": "EUR", // Slovakia
        "EE": "EUR", // Estonia
        "LV": "EUR", // Latvia
        "LT": "EUR", // Lithuania
        "HR": "EUR", // Croatia (joined Euro 2023)
        "AU": "AUD", // Australian Dollar
        "NZ": "NZD", // New Zealand Dollar
        "JP": "JPY", // Japanese Yen
        "CN": "CNY", // Chinese Yuan
        "IN": "INR", // Indian Rupee
        "BR": "BRL", // Brazilian Real
        "MX": "MXN", // Mexican Peso
        "KR": "KRW", // South Korean Won
        "SG": "SGD", // Singapore Dollar
        "HK": "HKD", // Hong Kong Dollar
        "CH": "CHF", // Swiss Franc
        "SE": "SEK", // Swedish Krona
        "NO": "NOK", // Norwegian Krone
        "DK": "DKK", // Danish Krone
        "PL": "PLN", // Polish Złoty
        "CZ": "CZK", // Czech Koruna
        "HU": "HUF", // Hungarian Forint
        "RO": "RON", // Romanian Leu
        "BG": "BGN", // Bulgarian Lev
        "RS": "RSD", // Serbian Dinar
        "TR": "TRY", // Turkish Lira
        "RU": "RUB", // Russian Ruble
        "ZA": "ZAR", // South African Rand
        "EG": "EGP", // Egyptian Pound
        "NG": "NGN", // Nigerian Naira
        "KE": "KES", // Kenyan Shilling
        "GH": "GHS", // Ghanaian Cedi
        "MA": "MAD", // Moroccan Dirham
        "TN": "TND", // Tunisian Dinar
        "DZ": "DZD", // Algerian Dinar
        "TH": "THB", // Thai Baht
        "VN": "VND", // Vietnamese Dong
        "ID": "IDR", // Indonesian Rupiah
        "MY": "MYR", // Malaysian Ringgit
        "PH": "PHP", // Philippine Peso
    };

    return currencyMap[countryCode.toUpperCase()] || "EUR"; // Default to EUR if country not found
};

/**
 * Helper to map currency ISO code (e.g., "USD") to backend currency id from Redux list
 */
export const findCurrencyIdByCode = (code: string, currencies: CurrencyData[]): string | undefined => {
    if (!code || !currencies || currencies.length === 0) return undefined;
    
    const upperCode = String(code).toUpperCase();
    
    // Try exact match on label or initial prop
    const exact = currencies.find((c: CurrencyData) =>
        (typeof c.label === "string" && c.label.toUpperCase() === upperCode) ||
        (typeof c.initial === "string" && c.initial.toUpperCase() === upperCode)
    );
    
    if (exact) return exact.value;
    
    // Try partial match in label (e.g., "US Dollar (USD)")
    const partial = currencies.find((c: CurrencyData) =>
        typeof c.label === "string" && c.label.toUpperCase().includes(upperCode)
    );
    
    return partial ? partial.value : undefined;
};

/**
 * Cache management functions
 */
const isCacheValid = (cacheData: any): boolean => {
    if (!cacheData || !cacheData.timestamp) return false;
    const now = new Date().getTime();
    const cacheTime = new Date(cacheData.timestamp).getTime();
    const hoursDiff = (now - cacheTime) / (1000 * 60 * 60);
    return hoursDiff < CACHE_EXPIRY_HOURS;
};

const getCachedData = (key: string): any => {
    try {
        const cached = localStorage.getItem(key);
        if (cached) {
            const data = JSON.parse(cached);
            return isCacheValid(data) ? data : null;
        }
    } catch (error) {
        console.error(`Error reading cache for ${key}:`, error);
    }
    return null;
};

const setCachedData = (key: string, data: any): void => {
    try {
        const cacheData = {
            ...data,
            timestamp: new Date().toISOString()
        };
        localStorage.setItem(key, JSON.stringify(cacheData));
    } catch (error) {
        console.error(`Error setting cache for ${key}:`, error);
    }
};

/**
 * Get cached currency preference or null if not available/expired
 */
export const getCachedCurrencyPreference = (): string | null => {
    const cached = getCachedData(CURRENCY_CACHE_KEY);
    return cached ? cached.currencyId : null;
};

export const getCachedCurrencyCode = (): string | null => {
    const cached = getCachedData(CURRENCY_CACHE_KEY);
    return cached && cached.currencyCode ? cached.currencyCode : null;
};

export const getCachedCurrencySymbol = (): string | null => {
    const cached = getCachedData(CURRENCY_CACHE_KEY);
    return cached && cached.currencySymbol ? cached.currencySymbol : null;
};

/**
 * Cache the user's currency preference
 */
export const setCachedCurrencyPreference = (
    currencyId: string,
    options?: { currencyCode?: string; currencySymbol?: string }
): void => {
    const payload: Record<string, any> = { currencyId };

    if (options?.currencyCode) {
        payload.currencyCode = options.currencyCode;
    }

    if (options?.currencySymbol) {
        payload.currencySymbol = options.currencySymbol;
    }

    setCachedData(CURRENCY_CACHE_KEY, payload);
};

/**
 * Get cached location data or null if not available/expired
 */
export const getCachedLocationData = (): LocationData | null => {
    const cached = getCachedData(LOCATION_CACHE_KEY);
    return cached ? { countryCode: cached.countryCode, countryName: cached.countryName } : null;
};

/**
 * Cache the user's location data
 */
export const setCachedLocationData = (locationData: LocationData): void => {
    setCachedData(LOCATION_CACHE_KEY, locationData);
};

/**
 * Get cached country preference or null if not available/expired
 */
export const getCachedCountryPreference = (): string | null => {
    const cached = getCachedData(COUNTRY_CACHE_KEY);
    return cached ? cached.countryId : null;
};

/**
 * Cache the user's country preference
 */
export const setCachedCountryPreference = (countryId: string): void => {
    setCachedData(COUNTRY_CACHE_KEY, { countryId });
};

/**
 * Detect user location using geolocation API
 */
const detectLocationByGeolocation = async (): Promise<LocationData | null> => {
    return new Promise((resolve) => {
        if (!navigator.geolocation) {
            resolve(null);
            return;
        }

        navigator.geolocation.getCurrentPosition(
            async (position) => {
                try {
                    const { latitude, longitude } = position.coords;

                    // Use reverse geocoding to get country from coordinates
                    const response = await fetch(
                        `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`
                    );

                    if (response.ok) {
                        const data = await response.json();
                        resolve({
                            countryCode: data.countryCode,
                            countryName: data.countryName
                        });
                    } else {
                        resolve(null);
                    }
                } catch (error) {
                    console.error("Error in geolocation detection:", error);
                    resolve(null);
                }
            },
            (error) => {
                console.error("Geolocation error:", error);
                resolve(null);
            },
            {
                timeout: 10000,
                enableHighAccuracy: false
            }
        );
    });
};

/**
 * Detect user location using backend API via Redux action
 * This function should be called with dispatch from Redux
 */
export const detectLocationByBackendAPI = async (dispatch: any): Promise<LocationData & { currencyId?: string } | null> => {
    try {
        // Import the Redux action dynamically to avoid circular dependencies
        const { detectUserLocation } = await import("../redux/Actions/AuthAction");
        
        // Call the Redux action
        const result = await dispatch(detectUserLocation({ isShowLoader: false }));
        
        if (detectUserLocation.fulfilled.match(result)) {
            const data = result.payload;
            
            return {
                countryCode: data.countryCode,
                countryName: data.countryName,
                currencyId: data.currencyId,
            };
        }
    } catch (error) {
        console.error("Backend location detection error:", error);
    }

    return null;
};

/**
 * Main function to detect user location and return currency information
 * @param currencies - Array of available currencies from Redux
 * @param dispatch - Redux dispatch function (required for API call)
 */
export const detectUserCurrency = async (
    currencies: CurrencyData[], 
    dispatch?: any
): Promise<{
    currencyId: string;
    currencyCode: string;
    locationData: LocationData | null;
    source: "cache" | "backend" | "default";
}> => {
    // First, check if we have a cached currency preference
    const cachedCurrencyId = getCachedCurrencyPreference();
    if (cachedCurrencyId) {
        const currency = currencies.find(c => c.value === cachedCurrencyId);
        if (currency) {
            return {
                currencyId: cachedCurrencyId,
                currencyCode: currency.initial,
                locationData: getCachedLocationData(),
                source: "cache"
            };
        }
    }

    // Check for cached location data
    const cachedLocation = getCachedLocationData();
    if (cachedLocation) {
        const detectedCurrency = getCurrencyByCountry(cachedLocation.countryCode);
        const detectedCurrencyId = findCurrencyIdByCode(detectedCurrency, currencies) || findCurrencyIdByCode("USD", currencies);
        
        if (detectedCurrencyId) {
            setCachedCurrencyPreference(detectedCurrencyId);
            return {
                currencyId: detectedCurrencyId,
                currencyCode: detectedCurrency,
                locationData: cachedLocation,
                source: "cache"
            };
        }
    }

    // Try backend API for location detection (only if dispatch is provided)
    if (dispatch) {
        const backendLocationData = await detectLocationByBackendAPI(dispatch);
        if (backendLocationData) {
            const detectedCurrency = getCurrencyByCountry(backendLocationData.countryCode);
            
            // Use currencyId from backend if available, otherwise find it
            let detectedCurrencyId = backendLocationData.currencyId;
            if (!detectedCurrencyId) {
                detectedCurrencyId = findCurrencyIdByCode(detectedCurrency, currencies) || findCurrencyIdByCode("USD", currencies);
            }
            
            if (detectedCurrencyId) {
                // Cache the results
                const locationData: LocationData = {
                    countryCode: backendLocationData.countryCode,
                    countryName: backendLocationData.countryName
                };
                setCachedLocationData(locationData);
                setCachedCurrencyPreference(detectedCurrencyId);
                
                return {
                    currencyId: detectedCurrencyId,
                    currencyCode: detectedCurrency,
                    locationData: locationData,
                    source: "backend"
                };
            }
        }
    }

    // Final fallback to EUR (base currency)
    const eurId = findCurrencyIdByCode("EUR", currencies);
    if (eurId) {
        setCachedCurrencyPreference(eurId);
        return {
            currencyId: eurId,
            currencyCode: "EUR",
            locationData: null,
            source: "default"
        };
    }

    // If even EUR is not found, return the first available currency
    const fallbackCurrency = currencies[0];
    return {
        currencyId: fallbackCurrency.value,
        currencyCode: fallbackCurrency.initial,
        locationData: null,
        source: "default"
    };
};

/**
 * Clear all cached currency and location data
 */
export const clearCurrencyCache = (): void => {
    localStorage.removeItem(CURRENCY_CACHE_KEY);
    localStorage.removeItem(LOCATION_CACHE_KEY);
};

/**
 * Get currency symbol from currency code
 * @param currencyCode - ISO currency code (e.g., "USD", "EUR", "GBP")
 * @returns Currency symbol (e.g., "$", "€", "£")
 */
export const getCurrencySymbol = (currencyCode: string): string => {
    const symbolMap: { [key: string]: string } = {
        "USD": "$",
        "CAD": "CA$",
        "GBP": "£",
        "EUR": "€",
        "AUD": "A$",
        "NZD": "NZ$",
        "JPY": "¥",
        "CNY": "¥",
        "INR": "₹",
        "BRL": "R$",
        "MXN": "MX$",
        "KRW": "₩",
        "SGD": "S$",
        "HKD": "HK$",
        "CHF": "CHF",
        "SEK": "kr",
        "NOK": "kr",
        "DKK": "kr",
        "PLN": "zł",
        "CZK": "Kč",
        "HUF": "Ft",
        "RON": "lei",
        "BGN": "лв",
        "RSD": "дин",
        "TRY": "₺",
        "RUB": "₽",
        "ZAR": "R",
        "EGP": "E£",
        "NGN": "₦",
        "KES": "KSh",
        "GHS": "GH₵",
        "MAD": "DH",
        "TND": "DT",
        "DZD": "DA",
        "THB": "฿",
        "VND": "₫",
        "IDR": "Rp",
        "MYR": "RM",
        "PHP": "₱",
    };

    if (!currencyCode) return "$";
    return symbolMap[currencyCode.toUpperCase()] || "$"; // Default to $ if not found
};

/**
 * Hook-like function to get currency with automatic detection
 * @param currencies - Array of available currencies from Redux
 * @param onCurrencyDetected - Optional callback when currency is detected
 * @param dispatch - Redux dispatch function (required for backend API call)
 */
export const useCurrencyDetection = async (
    currencies: CurrencyData[],
    onCurrencyDetected?: (currencyId: string, source: string) => void,
    dispatch?: any
): Promise<string> => {
    if (!currencies || currencies.length === 0) {
        return "1"; // Default fallback
    }

    try {
        const result = await detectUserCurrency(currencies, dispatch);
        
        if (onCurrencyDetected) {
            onCurrencyDetected(result.currencyId, result.source);
        }

        return result.currencyId;
    } catch (error) {
        console.error("Error in currency detection:", error);
        
        // Fallback to EUR (base currency) or first available currency
        const eurId = findCurrencyIdByCode("EUR", currencies);
        if (eurId) {
            return eurId;
        }
        
        return currencies[0]?.value || "1";
    }
};

/**
 * Get the dynamic currency symbol from localStorage
 * Reads the selected currency from localStorage and returns the corresponding symbol
 * Falls back to "$" if no currency is found
 * @returns Currency symbol string (e.g., "$", "€", "£", "₹")
 */
export const getDynamicCurrencySymbol = (): string => {
    try {
        const storedCurrency = localStorage.getItem("selectedCurrency");
        const storedCurrencyCode = localStorage.getItem("selectedCurrencyCode");
        
        if (storedCurrencyCode) {
            return getCurrencySymbol(storedCurrencyCode);
        }
        
        if (storedCurrency) {
            const currencyData = JSON.parse(storedCurrency);
            const code = currencyData.initial || currencyData.code || currencyData.currencyCode;
            if (code) {
                return getCurrencySymbol(code);
            }
        }
        
        const cachedCurrencyId = getCachedCurrencyPreference();
        if (cachedCurrencyId) {
            const currenciesStr = localStorage.getItem("currencies");
            if (currenciesStr) {
                const currencies = JSON.parse(currenciesStr);
                const currency = currencies.find((c: CurrencyData) => c.value === cachedCurrencyId);
                if (currency && currency.initial) {
                    return getCurrencySymbol(currency.initial);
                }
            }
        }
        
    } catch (error) {
        console.error("Error getting dynamic currency symbol:", error);
    }
    
    return "$";
};

/**
 * Get the dynamic currency code from localStorage
 * Reads the selected currency from localStorage and returns the corresponding code
 * Falls back to "USD" if no currency is found
 * @returns Currency code string (e.g., "USD", "EUR", "GBP", "INR")
 */
export const getDynamicCurrencyCode = (): string => {
    try {
        const storedCurrencyCode = localStorage.getItem("selectedCurrencyCode");
        if (storedCurrencyCode) {
            return storedCurrencyCode;
        }
        
        const storedCurrency = localStorage.getItem("selectedCurrency");
        if (storedCurrency) {
            const currencyData = JSON.parse(storedCurrency);
            const code = currencyData.initial || currencyData.code || currencyData.currencyCode;
            if (code) {
                return code;
            }
        }
        
        const cachedCurrencyId = getCachedCurrencyPreference();
        if (cachedCurrencyId) {
            const currenciesStr = localStorage.getItem("currencies");
            if (currenciesStr) {
                const currencies = JSON.parse(currenciesStr);
                const currency = currencies.find((c: CurrencyData) => c.value === cachedCurrencyId);
                if (currency && currency.initial) {
                    return currency.initial;
                }
            }
        }
        
    } catch (error) {
        console.error("Error getting dynamic currency code:", error);
    }
    
    return "USD";
};

/**
 * Get the user's preferred currency ID from cache
 * @returns Currency ID string or null
 */
export const getUserPreferredCurrencyId = (): string | null => {
    return getCachedCurrencyPreference();
};

/**
 * Format amount with currency conversion
 * Converts the amount from its original currency to the user's preferred currency and formats it
 * @param amount - The amount to format
 * @param originalCurrencyId - The original currency ID of the amount (optional)
 * @param currencies - Array of currency objects from Redux (optional)
 * @param showOriginal - Whether to show the original amount in parentheses (default: false)
 * @returns Formatted string with currency symbol and converted amount
 */
export const formatAmountWithConversion = (
    amount: number | null | undefined,
    originalCurrencyId?: string | null,
    currencies?: any[],
    showOriginal: boolean = false
): string => {
    // If amount is null/undefined/zero, return default
    if (!amount || amount === 0) return `${getDynamicCurrencySymbol()}0.00`;
    
    // If no original currency or currencies array, just format with dynamic symbol
    if (!originalCurrencyId || !currencies || currencies.length === 0) {
        return `${getDynamicCurrencySymbol()}${amount.toFixed(2)}`;
    }
    
    // Get user's preferred currency
    const userCurrencyId = getCachedCurrencyPreference();
    if (!userCurrencyId) {
        // No user preference, just show with dynamic symbol
        return `${getDynamicCurrencySymbol()}${amount.toFixed(2)}`;
    }
    
    // If same currency, no conversion needed
    if (userCurrencyId === originalCurrencyId) {
        return `${getDynamicCurrencySymbol()}${amount.toFixed(2)}`;
    }
    
    // Perform conversion using imported functions
    try {
        const conversion = convertCurrency(amount, originalCurrencyId, userCurrencyId, currencies);
        const targetCurrency = getCurrencyById(userCurrencyId, currencies);
        const symbol = getCurrencySymbol(targetCurrency?.initial || "USD");
        
        if (showOriginal) {
            const originalCurrency = getCurrencyById(originalCurrencyId, currencies);
            const originalSymbol = getCurrencySymbol(originalCurrency?.initial || "USD");
            return `${symbol}${conversion.convertedAmount.toFixed(2)} (${originalSymbol}${amount.toFixed(2)} ${originalCurrency?.initial})`;
        }
        
        return `${symbol}${conversion.convertedAmount.toFixed(2)}`;
    } catch (error) {
        console.error("Error in formatAmountWithConversion:", error);
        return `${getDynamicCurrencySymbol()}${amount.toFixed(2)}`;
    }
};
